import React from 'react';
import {
    Box,
    Typography,
    Paper,
    Container,
    Stack,
    styled,
    useTheme,
} from "@mui/material";
import { Link } from "react-router-dom";
import {
    Error as ErrorIcon,
    Home as HomeIcon,
    ShoppingCart as ShoppingCartIcon,
} from "@mui/icons-material";
import { keyframes } from "@mui/material/styles";

// Animations
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shakeAnimation = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
  20%, 40%, 60%, 80% { transform: translateX(5px); }
`;

// Styled Components
const PageContainer = styled(Box)(({ theme }) => ({
    minHeight: "70vh",
    background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.grey[100]} 100%)`,
    position: "relative",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4, 0),
}));

const ErrorContainer = styled(Container)(({ theme }) => ({
    position: "relative",
    zIndex: 2,
    animation: `${slideIn} 0.6s ease-out`,
}));

const MainCard = styled(Paper)(({ theme }) => ({
    position: "relative",
    overflow: "hidden",
    borderRadius: theme.shape.borderRadius * 3,
    backgroundColor: theme.palette.background.paper,
    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08)",
}));

const HeaderSection = styled(Box)(({ theme }) => ({
    background: `linear-gradient(135deg, ${theme.palette.error.dark} 0%, ${theme.palette.error.main} 100%)`,
    padding: theme.spacing(4),
    color: theme.palette.common.white,
    position: "relative",
    overflow: "hidden",
    textAlign: "center",
    "&::after": {
        content: '""',
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        height: "30%",
        background: "linear-gradient(transparent, rgba(0,0,0,0.1))",
    },
}));

const ErrorIconContainer = styled(Box)(({ theme }) => ({
    width: 80,
    height: 80,
    borderRadius: "50%",
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto",
    marginBottom: theme.spacing(2),
    animation: `${shakeAnimation} 0.8s ease-in-out`,
}));

const ContentSection = styled(Box)(({ theme }) => ({
    padding: theme.spacing(4),
    textAlign: "center",
}));

const ActionButton = styled(Link)(({ theme, variant }) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(1.5, 3),
    borderRadius: theme.shape.borderRadius * 2,
    fontSize: "1rem",
    fontWeight: 600,
    textDecoration: "none",
    transition: "all 0.3s ease",
    ...(variant === "primary"
        ? {
            backgroundColor: theme.palette.error.main,
            color: theme.palette.common.white,
            "&:hover": {
                backgroundColor: theme.palette.error.dark,
                transform: "translateY(-2px)",
            },
        }
        : {
            backgroundColor: theme.palette.grey[50],
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.divider}`,
            "&:hover": {
                backgroundColor: theme.palette.grey[100],
                transform: "translateY(-2px)",
            },
        }),
}));

const UnifiedError = () => {
    const theme = useTheme();

    return (
        <PageContainer>
            <ErrorContainer maxWidth="sm">
                <MainCard>
                    <HeaderSection>
                        <ErrorIconContainer>
                            <ErrorIcon sx={{ fontSize: 40 }} />
                        </ErrorIconContainer>
                        <Typography variant="h4" gutterBottom fontWeight="bold">
                            Payment Failed
                        </Typography>
                        <Typography variant="h6" sx={{ opacity: 0.9 }}>
                            Oops! Something went wrong
                        </Typography>
                    </HeaderSection>

                    <ContentSection>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                            We couldn't process your payment at this time. Please check your payment details and try again.
                            If the problem persists, contact our support team.
                        </Typography>

                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                gap: 2,
                                flexWrap: "wrap",
                            }}
                        >
                            <ActionButton to="/checkout" variant="primary">
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <ShoppingCartIcon />
                                    <span>Try Again</span>
                                </Stack>
                            </ActionButton>
                            <ActionButton to="/">
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <HomeIcon />
                                    <span>Back to Home</span>
                                </Stack>
                            </ActionButton>
                        </Box>
                    </ContentSection>
                </MainCard>
            </ErrorContainer>
        </PageContainer>
    );
};

export default UnifiedError;
